import { CmsRedirection } from '@/src/cms/domain/models/cms-redirection';

import { logException } from '../../utils/log';

import type { SsrData, SsrErrors } from './ssr-data';
import type { LogAndSaveError, SsrDataFactory } from './ssr-data-factory';
import type { JsonObject } from '../../utils/serialization.types';


export class SsrDataBuilder {
  ssrData: SsrData = {};
  ssrSerializedData: SsrData = {};
  ssrErrors: SsrErrors = {};

  constructor(private readonly factories: SsrDataFactory[]) {}

  async createData() {
    await Promise.all(
      this.factories.map(async (factory) => {
        this.ssrData[factory.key] = await factory.create({
          ssrData: this.ssrData,
          logAndSaveError: this.logAndSaveError
        });
      })
    );
  }

  async appendData<ResultType>(factory: SsrDataFactory<ResultType>) {
    this.factories.push(factory);
    this.ssrData[factory.key] = await factory.create({
      ssrData: this.ssrData,
      logAndSaveError: this.logAndSaveError
    });
  }

  serializeData() {
    for (const [key, value] of Object.entries(this.ssrData)) {
      const factory = this.getFactory(key);
      this.ssrSerializedData[key] = factory.serialize(value);
    }
  }

  deserializeData(serializedData: SsrData) {
    this.ssrSerializedData = serializedData;
    for (const [key, value] of Object.entries(serializedData)) {
      const factory = this.getFactory(key);
      this.ssrData[key] = factory.deserialize(value as JsonObject);
    }
  }

  private logAndSaveError: LogAndSaveError = (key, error) => {
    if (!(error instanceof CmsRedirection)) {
      logException(`Error in ${key} SSR Data Builder`, error);
    }
    this.ssrErrors[key] = error;
  };

  private getFactory(key: string): SsrDataFactory<unknown> {
    const factory = this.factories.find((factory) => factory.key === key);
    if (!factory) {
      throw new Error(`SSRDataFactory with key ${key} not found`);
    }
    return factory;
  }
}
